import type { ReconciliationSession } from '../types'
import { DOC_LABEL } from './doc-constants'

export interface DocColumn {
  docKey: string
  label: string
  filename: string | null
  documentId: string | null
  supplierName: string | null
  supplierGln: string | null
  supplierIban: string | null
  supplierVat: string | null
  supplierKvk: string | null
  buyerName: string | null
  buyerGln: string | null
  buyerVat: string | null
  invoicerName: string | null
  invoicerGln: string | null
  invoiceeName: string | null
  invoiceeGln: string | null
  deliveryPartyName: string | null
  deliveryPartyGln: string | null
  shipFromName: string | null
  shipFromGln: string | null
  projectNumber: string | null
  missingProject: boolean | null
  buyersOrderNumber: string | null
  suppliersOrderNumber: string | null
  endCustomerOrderNumber: string | null
  refs: { label: string; value: string | null }[]
  hasPdf: boolean
}

export type Deviations = Record<keyof Pick<DocColumn,
  'supplierName' | 'supplierGln' | 'buyerName' | 'buyerGln' | 'projectNumber'
>, boolean[]>

export function truncateFilename(name: string, maxLen = 40): string {
  if (name.length <= maxLen) return name
  const front = Math.round(maxLen * 0.70)
  const tail  = Math.round(maxLen * 0.05)
  return `${name.slice(0, front)}…${name.slice(-tail)}`
}

export function findDeviating(values: (string | null)[]): boolean[] {
  const nonNull = values.filter((v): v is string => v !== null)
  if (nonNull.length <= 1) return values.map(() => false)
  const freq = new Map<string, number>()
  for (const v of nonNull) freq.set(v, (freq.get(v) ?? 0) + 1)
  const maxFreq = Math.max(...freq.values())
  const majorityValue = [...freq.entries()].find(([, c]) => c === maxFreq)?.[0]
  return values.map(v => v !== null && v !== majorityValue)
}

export function computeDeviations(cols: DocColumn[]): Deviations {
  const fields = ['supplierName', 'supplierGln', 'buyerName', 'buyerGln', 'projectNumber'] as const
  return Object.fromEntries(
    fields.map(f => [f, findDeviating(cols.map(c => c[f]))])
  ) as Deviations
}

export function buildColumns(session: ReconciliationSession): DocColumn[] {
  return [
    {
      docKey: 'order_response',
      label: DOC_LABEL.order_response,
      filename: session.ordrsp_filename,
      documentId: session.order_response_number,
      supplierName: session.ordrsp_supplier_name,
      supplierGln: session.ordrsp_supplier_gln,
      supplierIban: session.ordrsp_supplier_iban,
      supplierVat: session.ordrsp_supplier_vat,
      supplierKvk: session.ordrsp_supplier_kvk,
      buyerName: session.ordrsp_buyer_name,
      buyerGln: session.ordrsp_buyer_gln,
      buyerVat: null,
      invoicerName: null, invoicerGln: null,
      invoiceeName: null, invoiceeGln: null,
      deliveryPartyName: session.ordrsp_delivery_party_name,
      deliveryPartyGln: session.ordrsp_delivery_party_gln,
      shipFromName: null,
      shipFromGln: null,
      projectNumber: session.ordrsp_project_number,
      missingProject: session.ordrsp_missing_project,
      buyersOrderNumber: session.ordrsp_buyers_order_number,
      suppliersOrderNumber: session.ordrsp_suppliers_order_number,
      endCustomerOrderNumber: session.ordrsp_end_customer_order_number,
      refs: [{ label: 'Ref. OrderResponse', value: session.order_response_number }],
      hasPdf: session.ordrsp_has_pdf,
    },
    {
      docKey: 'despatch_advice',
      label: DOC_LABEL.despatch_advice,
      filename: session.desadv_filename,
      documentId: session.desadv_number,
      supplierName: session.desadv_supplier_name,
      supplierGln: session.desadv_supplier_gln,
      supplierIban: session.desadv_supplier_iban,
      supplierVat: session.desadv_supplier_vat,
      supplierKvk: session.desadv_supplier_kvk,
      buyerName: session.desadv_buyer_name,
      buyerGln: session.desadv_buyer_gln,
      buyerVat: null,
      invoicerName: null, invoicerGln: null,
      invoiceeName: null, invoiceeGln: null,
      deliveryPartyName: session.desadv_delivery_party_name,
      deliveryPartyGln: session.desadv_delivery_party_gln,
      shipFromName: session.desadv_ship_from_name,
      shipFromGln: session.desadv_ship_from_gln,
      projectNumber: session.desadv_project_number,
      missingProject: session.desadv_missing_project,
      buyersOrderNumber: session.desadv_buyers_order_number,
      suppliersOrderNumber: null,
      endCustomerOrderNumber: session.desadv_end_customer_order_number,
      refs: [
        { label: 'Ref. OrderResponse', value: session.desadv_ordrsp_ref },
        { label: 'Ref. DespatchAdvice', value: session.desadv_number },
      ],
      hasPdf: session.desadv_has_pdf,
    },
    {
      docKey: 'invoice',
      label: DOC_LABEL.invoice,
      filename: session.invoice_filename,
      documentId: session.invoice_number,
      supplierName: session.invoice_supplier_name,
      supplierGln: session.invoice_supplier_gln,
      supplierIban: session.invoice_supplier_iban,
      supplierVat: session.invoice_supplier_vat,
      supplierKvk: session.invoice_supplier_kvk,
      buyerName: session.invoice_buyer_name,
      buyerGln: session.invoice_buyer_gln,
      buyerVat: session.invoice_buyer_vat,
      invoicerName: session.invoice_invoicer_name,
      invoicerGln: session.invoice_invoicer_gln,
      invoiceeName: session.invoice_invoicee_name,
      invoiceeGln: session.invoice_invoicee_gln,
      deliveryPartyName: session.invoice_delivery_party_name,
      deliveryPartyGln: session.invoice_delivery_party_gln,
      shipFromName: session.invoice_ship_from_name,
      shipFromGln: session.invoice_ship_from_gln,
      projectNumber: session.invoice_project_number,
      missingProject: session.invoice_missing_project,
      buyersOrderNumber: session.invoice_buyers_order_number,
      suppliersOrderNumber: null,
      endCustomerOrderNumber: session.invoice_end_customer_order_number,
      refs: [
        { label: 'Ref. OrderResponse', value: session.invoice_ordrsp_ref },
        { label: 'Ref. DespatchAdvice', value: session.invoice_desadv_ref },
      ],
      hasPdf: session.invoice_has_pdf,
    },
  ]
}
